import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { RefreshCw, Save, X } from 'lucide-react';

interface ConcurrentEditBannerProps {
  onReload: () => void;
  onDismiss: () => void;
  onSaveMyChanges?: () => void;
  hasUnsavedChanges?: boolean;
  documentLabel?: string;
  positionClassName?: string;
  isSaving?: boolean;
}

export default function ConcurrentEditBanner({
  onReload,
  onDismiss,
  onSaveMyChanges,
  hasUnsavedChanges,
  documentLabel,
  positionClassName = 'top-20',
  isSaving = false
}: ConcurrentEditBannerProps) {
  const { t } = useTranslation();
  const { getColour, theme } = useThemeContext();

  // eslint-disable-next-line no-restricted-syntax
  const bgColor = theme === 'dark' ? '#1a2a4a' : '#e8f0fe';

  return (
    <div
      role="alert"
      className={`fixed ${positionClassName} right-4 z-9999 flex flex-col gap-2 rounded-2xl px-3 py-3 text-sm`}
      style={{
        backgroundColor: bgColor,
        color: getColour('normalText'),
        boxShadow: `0 4px 24px rgba(0,0,0,0.18), 0 1px 4px ${getColour('activeText')}30`,
        minWidth: '280px',
        maxWidth: '340px'
      }}
    >
      <span className="font-semibold">
        {documentLabel ? `${documentLabel}: ` : ''}
        {t('document-updated-remotely')}
      </span>

      <span className="text-xs" style={{ color: getColour('inactiveText') }}>
        {t('concurrent-edit-autosave-paused')}
      </span>

      <div className="flex items-center gap-2 flex-wrap">
        {hasUnsavedChanges && onSaveMyChanges && (
          <button
            onClick={onSaveMyChanges}
            disabled={isSaving}
            className="flex items-center gap-1 px-3 py-1.5 rounded-2xl text-xs font-medium hover:opacity-80 disabled:opacity-50 transition-opacity"
            style={{
              backgroundColor: getColour('activeText'),
              color: getColour('thirdLayer')
            }}
          >
            <Save size={12} />
            {t('save-my-changes')}
          </button>
        )}

        <button
          onClick={onReload}
          disabled={isSaving}
          className="flex items-center gap-1 px-3 py-1.5 rounded-2xl text-xs font-medium hover:opacity-80 disabled:opacity-50 transition-opacity"
          style={
            hasUnsavedChanges
              ? {
                  backgroundColor: getColour('border'),
                  color: getColour('normalText')
                }
              : {
                  backgroundColor: getColour('activeText'),
                  color: getColour('thirdLayer')
                }
          }
        >
          <RefreshCw size={12} />
          {t('reload')}
        </button>

        <button
          onClick={onDismiss}
          className="flex items-center gap-1 px-3 py-1.5 rounded-2xl text-xs hover:opacity-80 transition-opacity"
          style={{
            backgroundColor: getColour('border'),
            color: getColour('normalText')
          }}
        >
          <X size={12} />
          {t('dismiss')}
        </button>
      </div>
    </div>
  );
}
